import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  removeFederation,
  setFederation,
  type TenantFederation,
  type TenantProvisioning,
} from "./provisioningApi";
import {
  FEDERATION_FAILED,
  FEDERATION_NOT_CONFIGURED,
  federationBodyKey,
  federationKey,
} from "./provisioningSteps";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { formatDate } from "@web/lib/date";

// ADR-0014 §7.2 / §12 C-6 — the OTHER half of provisioning step 2: the customer's own identity provider.
//
// WHY IT IS BESIDE STEP 2 AND NOT A STEP OF ITS OWN. §7.1 numbers four steps, and a customer with no
// identity provider of their own is not half-provisioned — plenty of them will never have one, and their
// people sign in with the platform realm's credentials as every tenant provisioned before C-6 does. A fifth
// step would put every one of those customers on the directory's unfinished list for something nobody has
// to do. So it is a sub-section with its own state, and the only thing that reopens step 2 is a federation
// the provisioning component REFUSED, which is the one case an operator fixes.
//
// WHAT THIS FORM DOES NOT DO, AND SAYS SO. The save does not configure Keycloak: the request-path API holds
// no realm authority (§7.2), so it records the arrangement and the separately credentialed provisioning
// component writes the realm IdP on its next pass. The state therefore comes back `pending_apply`, and the
// sentence under the badge says that in the operator's own language rather than letting a 200 read as
// "done". An operator who is told "saved" and then finds their customer cannot sign in has been lied to by
// a console that reported a success it did not have.
//
// THERE ARE THREE FIELDS AND NO ENDPOINTS. The authorization, token and JWKS URLs are resolved from the
// issuer's discovery document when the component applies, so the operator cannot mistype one of five URLs
// into a federation that fails at a person's first sign-in — and the customer can move an endpoint without
// Verso editing a row.
//
// THE SECRET FIELD IS NEVER PRE-FILLED, and that is not a convenience choice. The API may WRITE the
// customer's client secret to OpenBao and may not read it back (`ITenantIdpSecretStore`), so there is
// nothing to pre-fill it FROM; a form offering "leave blank to keep the current secret" would be promising
// something no code on either side can deliver. Re-entering it on every save is also what makes a rotation
// an ordinary save rather than a second operation.
//
// ITS BUSY STATE IS ITS OWN, unlike the three acts on the invite above it, which share the section's
// `busy`. Nothing here and nothing there writes the same row twice in a way the other would lose — both
// are idempotent re-assertions and both answer with the WHOLE status, which the parent re-renders from —
// so the cost of the simpler arrangement is that an operator can have an invite and a federation save in
// flight at once, and the later answer wins. The alternative was threading two more actions through a
// shared union for no outcome anybody would notice.

/** The problem-details `error` on the 409 of a resend/cancel; shared shape, read for its `detail` only. */
interface FederationProblem {
  detail?: string;
}

function problemDetail(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const problem = err.problem as FederationProblem | undefined;
  return typeof problem?.detail === "string" && problem.detail.trim() !== ""
    ? problem.detail
    : undefined;
}

/**
 * Which message this failure gets. Every one of these is its own sentence, for the reason step 2's
 * refusals are: 404 says the page is out of date, 422 says a field is wrong, 502 says Verso's own secret
 * store did not take the value and NOTHING was recorded — which is the one an operator must not read as
 * "the customer's identity provider refused", because the customer's identity provider was never asked.
 *
 * The 409 is the one that is NOT a retry (`federation_changed_concurrently`): somebody else saved or
 * withdrew an arrangement between this form's read and its write, so nothing was recorded and what is in
 * place now may be the answer that should stand. Its sentence says reload, because pressing the button
 * again would record this operator's values over a decision they have not seen.
 */
function federationErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return "tenants.provisioning.federation.errorNetwork";
  if (err.status === 404) return "tenants.provisioning.federation.error404";
  if (err.status === 409) return "tenants.provisioning.federation.error409";
  if (err.status === 422) return "tenants.provisioning.federation.error422";
  if (err.status === 502) return "tenants.provisioning.federation.error502";
  return "tenants.provisioning.federation.errorNetwork";
}

export function TenantFederationForm({
  tenantId,
  federation,
  disabled,
  frozen,
  onChanged,
}: {
  tenantId: string;
  federation: TenantFederation;
  /** Another step's request is in flight; the parent disables the whole section while one is. */
  disabled: boolean;
  /** The tenant is frozen: the arrangement still shows, the form does not. */
  frozen: boolean;
  onChanged: (provisioning: TenantProvisioning) => void;
}) {
  const { t } = useTranslation();
  const [issuer, setIssuer] = useState(federation.issuer ?? "");
  const [clientId, setClientId] = useState(federation.clientId ?? "");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState<"save" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const configured = federation.state !== FEDERATION_NOT_CONFIGURED;

  /* The state, as a badge and a sentence. The sentence is the load-bearing half here even more than it is
     on the activation detail: two of the four states are not the operator's to act on at all, and a badge
     reading "odottaa" with nothing under it invites exactly the retry that would change nothing. */
  const state = (
    <p className="provisioning__bound-state" data-testid="provisioning-federation-state">
      <span
        className={`badge ${
          federation.state === FEDERATION_FAILED
            ? "badge--status-draft"
            : federation.state === FEDERATION_NOT_CONFIGURED
              ? "badge--status-in_progress"
              : "badge--user-active"
        }`}
        data-testid="provisioning-federation-badge"
      >
        {t(federationKey(federation.state))}
      </span>{" "}
      {t(federationBodyKey(federation.state))}
    </p>
  );

  /* What is recorded. The ALIAS is here because it is what an operator looks for in the Keycloak console
     and what a tenant-addressed sign-in URL carries as `kc_idp_hint`; leaving it to a runbook would make
     the one value both ends must agree on the one value the console never shows. */
  const recorded = configured && (
    <dl className="provisioning__federation-facts" data-testid="provisioning-federation-facts">
      <dt>{t("tenants.provisioning.federation.issuerLabel")}</dt>
      <dd>{federation.issuer}</dd>
      <dt>{t("tenants.provisioning.federation.clientIdLabel")}</dt>
      <dd>{federation.clientId}</dd>
      <dt>{t("tenants.provisioning.federation.aliasLabel")}</dt>
      <dd>{federation.alias}</dd>
      <dt>{t("tenants.provisioning.federation.requestedAtLabel")}</dt>
      <dd>
        {federation.requestedAt ? formatDate(new Date(federation.requestedAt)) : "–"}
      </dd>
      <dt>{t("tenants.provisioning.federation.appliedAtLabel")}</dt>
      <dd>{federation.appliedAt ? formatDate(new Date(federation.appliedAt)) : "–"}</dd>
    </dl>
  );

  async function run(
    action: "save" | "remove",
    request: () => Promise<TenantProvisioning>,
    successKey: string,
  ) {
    setBusy(action);
    setError(null);
    setDetail(null);
    setNote(null);
    try {
      const updated = await request();
      // The secret is cleared on success, never kept in a controlled input between saves: it is the one
      // value on this page that the page itself must not be able to hand back.
      setClientSecret("");
      setNote(t(successKey));
      onChanged(updated);
    } catch (err) {
      // Silently, as every other request on this console does: the api layer has already started the
      // re-authentication, and an error message under a form the operator is about to be redirected away
      // from is noise.
      if (err instanceof SessionExpiredError) return;

      setError(t(federationErrorKey(err)));
      // The server's own sentence, when it has one: a 422 names the field it refused and a 502 says what
      // could not be stored, and neither is reconstructible from the status.
      setDetail(problemDetail(err) ?? null);
    } finally {
      setBusy(null);
    }
  }

  const feedback = (
    <>
      {note && (
        <p className="banner banner--success" role="status" data-testid="provisioning-federation-note">
          {note}
        </p>
      )}
      {error && (
        <p className="field-error" role="alert" data-testid="provisioning-federation-error">
          {error}
        </p>
      )}
      {detail && (
        <p className="provisioning__server-detail" data-testid="provisioning-federation-detail">
          {detail}
        </p>
      )}
      {/* The provisioning component's own last refusal, which is the only thing the console can say about a
          failed apply: the component writes no audit row — it holds no audit credential, and the audited
          act is the operator's decision rather than the machine's retry. */}
      {federation.error && (
        <p
          className="provisioning__server-detail"
          role="alert"
          data-testid="provisioning-federation-apply-error"
        >
          {federation.error}
        </p>
      )}
    </>
  );

  if (frozen) {
    return (
      <section className="provisioning__federation" data-testid="provisioning-federation">
        <h4 className="provisioning__federation-title">
          {t("tenants.provisioning.federation.legend")}
        </h4>
        {state}
        {recorded}
        <p className="provisioning__hint" role="note" data-testid="provisioning-federation-frozen">
          {t("tenants.provisioning.frozenReadOnly")}
        </p>
      </section>
    );
  }

  return (
    <section className="provisioning__federation" data-testid="provisioning-federation">
      <h4 className="provisioning__federation-title">
        {t("tenants.provisioning.federation.legend")}
      </h4>
      {state}
      {recorded}
      {feedback}

      {/* `noValidate` with the fields still typed, for the reason the invite form gives: the browser's own
          constraint check would refuse the submit with a tooltip and the operator would never see this
          app's message. The backend validates regardless (422). */}
      <form
        className="form provisioning__form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void run(
            "save",
            () => setFederation(tenantId, { issuer, clientId, clientSecret }),
            "tenants.provisioning.federation.saved",
          );
        }}
      >
        <label htmlFor="provisioning-federation-issuer">
          {t("tenants.provisioning.federation.issuerLabel")}
        </label>
        <input
          id="provisioning-federation-issuer"
          type="url"
          value={issuer}
          onChange={(e) => setIssuer(e.target.value)}
          autoComplete="off"
          aria-describedby="provisioning-federation-issuer-hint"
          data-testid="provisioning-federation-issuer"
        />
        <p id="provisioning-federation-issuer-hint" className="provisioning__hint">
          {t("tenants.provisioning.federation.issuerHint")}
        </p>

        <label htmlFor="provisioning-federation-client-id">
          {t("tenants.provisioning.federation.clientIdLabel")}
        </label>
        <input
          id="provisioning-federation-client-id"
          type="text"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          autoComplete="off"
          data-testid="provisioning-federation-client-id"
        />

        <label htmlFor="provisioning-federation-client-secret">
          {t("tenants.provisioning.federation.clientSecretLabel")}
        </label>
        <input
          id="provisioning-federation-client-secret"
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          autoComplete="new-password"
          aria-describedby="provisioning-federation-secret-hint"
          data-testid="provisioning-federation-client-secret"
        />
        <p id="provisioning-federation-secret-hint" className="provisioning__hint">
          {t("tenants.provisioning.federation.clientSecretHint")}
        </p>

        <div className="provisioning__step-actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={disabled || busy !== null}
            data-testid="provisioning-federation-submit"
          >
            {busy === "save"
              ? t("tenants.provisioning.federation.saving")
              : configured
                ? t("tenants.provisioning.federation.update")
                : t("tenants.provisioning.federation.save")}
          </button>

          {/* Offered only when there is something to withdraw. The operation is idempotent, so a button on
              a tenant with no federation would be safe and would also be an action that reaches nothing —
              which is the thing this console's own §7.1 note says not to show. */}
          {configured && (
            <button
              type="button"
              className="btn btn--danger"
              disabled={disabled || busy !== null}
              onClick={() =>
                void run(
                  "remove",
                  () => removeFederation(tenantId),
                  "tenants.provisioning.federation.removed",
                )
              }
              data-testid="provisioning-federation-remove"
            >
              {busy === "remove"
                ? t("tenants.provisioning.federation.removing")
                : t("tenants.provisioning.federation.remove")}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
